import { Calculation } from 'final-form-calculate'

import { CSSProperties } from 'react'
import { AppCollectionsState } from '../../collections/types/app'
import { Collection } from '../../collections/types/collection'
import { FieldTypes } from '../../constants/FieldTypes'
import { type CustomElement, type Descendant } from '../../editor/types'
import { BloodPressure, ExamSectionData } from '../../gyn/encounters/types/shared'
import { BaseUINode } from '../app/ui'
import { FileDBValue, ValidationErrors } from '../data'
import { AnyObject, FieldHints, FieldLabels, FieldMapValue, WithAlternate, WithId } from '../utils'
export type ShortcutItem<T = any, Y = any> = (data: T) => Y

export interface DropdownOptionItem<T = string> {
  text: string
  id: T
  keywords?: string[]
  redFlagReason?: string
  disabled?: boolean
}

export interface DropdownOptionGroup<T = string> {
  title: string
  options: DropdownOption<T>[]
}

export const isDropdownOptionItem = (
  option: DropdownOption<any>,
): option is DropdownOptionItem<any> => 'id' in option && !('options' in option)

export type DropdownOption<T = string> = DropdownOptionItem<T> | DropdownOptionGroup<T>

export interface FieldBlueprint {
  placeholder: string
  optional?: boolean
  labels?: FieldLabels
  condition?: FieldCondition
}
export interface FileFieldBlueprint extends FieldBlueprint {
  fileType?: FileType
  name: string
}
export interface AlternateFieldBlueprint extends FieldBlueprint {
  fieldType: FieldTypes
  altPrefix?: string
}

export type FieldCondition = (value?: FieldMapValue) => boolean
export type Validator<FieldType> = (value?: any, field?: FieldType) => string | undefined

/** Sizing knob for form inputs — controls font size, padding, and spacing. Defaults to `'md'`. */
export type InputSize = 'xs' | 'sm' | 'md' | 'lg'

export interface FieldPreset {
  title: string
  data: any
}
export interface BaseField {
  itemFields?: never
  autocomplete?: string
  shortcut?: never
  children?: never
  info?: never
  placeholder: string
  presets?: Array<FieldPreset> | ((value?: FieldMapValue) => Array<FieldPreset>)
  optional?: boolean
  labels?: FieldLabels
  protected?: boolean
  hints?: FieldHints
  warningMessage?: string
  condition?: FieldCondition | null
  format?: (value?: any) => any
  parse?: (value?: any) => any
  validate?: (value?: any) => string | undefined

  /** Per-field override of the form-wide input size. Falls back to the form/theme default. */
  size?: InputSize

  //layout
  row?: {
    index: number
    flex: number
  }
}

export type FileType = 'image' | 'pdf'
export interface PathPrefix {
  item: string
  location: string
}

export type TextField = BaseField & {
  type: FieldTypes.TEXT
  /** Enable dot-phrase / template expansion ('.'-picker + keyword+Tab) for this field. */
  withDotPhrases?: boolean
}

export interface PhoneField extends BaseField {
  type: FieldTypes.PHONE
}

export interface EmailField extends BaseField {
  type: FieldTypes.EMAIL
}

export type TextAreaField = BaseField & {
  type: FieldTypes.TEXTAREA
  /** Enable dot-phrase / template expansion ('.'-picker + keyword+Tab) for this field. */
  withDotPhrases?: boolean
  minRows?: number
}

export interface FileField extends BaseField {
  type: FieldTypes.FILE
  fileType?: FileType
  name: string
}

export interface DynamicDropdownField<Data extends AnyObject> extends BaseField {
  type: FieldTypes.DYNAMIC_DROPDOWN
  inputType?: 'native' | 'menu' | 'radio'
  baseId: string
  listId: string
  collection: Collection<Data>
  notApplicableText?: string
  allowOther?: boolean
}

export interface DateField extends BaseField {
  type: FieldTypes.DATE
  nativeInput?: boolean
  minDate?: number | 'now'
  maxDate?: number | 'now'
  defaultToNow?: boolean
  withNowButton?: boolean
  isoFormat?: boolean
}

// value: hh:mm:ss[gmtOffset]
export interface TimeField extends BaseField {
  type: FieldTypes.TIME
  minTime?: string
  maxTime?: string
  defaultToNow?: boolean
  withNowButton?: boolean
}

export interface DateTimeField extends BaseField {
  type: FieldTypes.DATETIME
  minDate?: number | 'now'
  defaultToNow?: boolean
  withNowButton?: boolean
  maxDate?: number | 'now'
  isoFormat?: boolean
}

export interface DropdownField<T = string, UINode extends BaseUINode = BaseUINode>
  extends BaseField {
  type: FieldTypes.DROPDOWN
  options: DropdownOption<T>[]
  allowMultiple?: boolean
  inputType?: 'native' | 'menu' | 'radio' | 'buttons'
  getKey?: (item: T) => string
  searchable?: boolean
  renderOption?: (item: DropdownOptionItem<T>) => UINode
  withNoneOption?: boolean
  allowOther?: boolean
  // Layout for `inputType: 'radio'` — 'row' (horizontal, wraps) or 'column' (vertical stack).
  radioDirection?: 'row' | 'column'
}

// Extract the union of ids from DropdownOptionItem
type ExtractOptionIds<T> =
  T extends DropdownOptionItem<infer U>
    ? U
    : T extends DropdownOptionGroup<infer V>
      ? ExtractOptionIds<V>
      : never

// Create a type that extracts the ids from a DropdownField
export type DropdownFieldValue<T extends DropdownField<any>> = ExtractOptionIds<
  T['options'][number]
>

export interface MultipleSelectField<UINode extends BaseUINode = BaseUINode> extends BaseField {
  type: FieldTypes.MULTIPLE_SELECT
  options: DropdownOptionItem[]
  renderOption?: (item: DropdownOptionItem) => UINode
}

export interface IdField<Data extends AnyObject, UINode extends BaseUINode = BaseUINode>
  extends BaseField {
  type: FieldTypes.ID
  collection: Collection<Data>
  inputType?: 'native' | 'menu' | 'radio'
  renderOption?: (item: WithId<Data>) => UINode
  withNoneOption?: boolean
  allowOther?: boolean
  allowMultiple?: boolean
  getItemText?: (item: any) => string
  searchable?: boolean
  // renderItem: (item: T) => React.ReactNode
  // ^ unimplemented but could be used to extend IdField
}

export interface BooleanField extends BaseField {
  type: FieldTypes.BOOLEAN
  yesText?: string
  noText?: string
  inputType?: DropdownField['inputType']
  redFlagYesReason?: string
  redFlagNoReason?: string
}

export interface CheckboxField extends BaseField {
  type: FieldTypes.CHECKBOX
  redFlagYesReason?: string
  redFlagNoReason?: string
  inputType?: 'dropdown' | 'switch'
  yesText?: string
  noText?: string
}

export interface BaseNumberField extends BaseField {
  max?: number
  min?: number
  defaultValue?: number
}

export interface NumberField extends BaseNumberField {
  type: FieldTypes.NUMBER
  step?: number
  numDigits?: number
  precision?: number
  withStepper?: boolean
  unit?: string
}

export interface HeightField extends BaseField {
  type: FieldTypes.HEIGHT
  defaultValue?: number
}

export interface PercentageField extends BaseNumberField {
  type: FieldTypes.PERCENTAGE
  step?: number
  precision?: number
}

export interface DollarAmountField extends BaseNumberField {
  type: FieldTypes.DOLLAR_AMOUNT
}

// doesnt support list yet
export interface AlternateField extends BaseField {
  type: FieldTypes.ALTERNATE
  fieldType: FieldTypes
  altPrefix?: string
}

export interface BaseSignatureField extends BaseField {
  type: FieldTypes.SIGNATURE
}
export interface SignatureField extends BaseSignatureField {
  expectedFirstName: string
  expectedMiddleInitial?: string
  expectedLastName: string
}

export interface BaseInitialsField extends BaseField {
  type: FieldTypes.INITIALS
}
export interface InitialsField extends BaseInitialsField {
  expectedInitials: string
  patientName: string
}

export interface ExamSectionField extends BaseField {
  type: FieldTypes.EXAM_SECTION
}

export interface BloodPressureField extends BaseField {
  type: FieldTypes.BLOOD_PRESSURE
}

export type Field =
  | AlternateField
  | BloodPressureField
  | BooleanField
  | CheckboxField
  | DateField
  | DateTimeField
  | DollarAmountField
  | DropdownField<any>
  | DynamicDropdownField<any>
  | EmailField
  | ExamSectionField
  | FileField
  | IdField<any>
  | InitialsField
  | MultipleSelectField
  | NumberField
  | HeightField
  | PercentageField
  | PhoneField
  | SignatureField
  | TextField
  | TextAreaField
  | TimeField

export interface FieldData {
  [FieldTypes.ALTERNATE]: WithAlternate<any>
  [FieldTypes.BOOLEAN]: boolean
  [FieldTypes.CHECKBOX]: boolean
  [FieldTypes.DATE]: number | string
  [FieldTypes.DATETIME]: number | string
  [FieldTypes.DOLLAR_AMOUNT]: number
  [FieldTypes.DROPDOWN]: string | string[]
  [FieldTypes.DYNAMIC_DROPDOWN]: string | string[]
  [FieldTypes.EMAIL]: string
  [FieldTypes.FILE]: FileDBValue
  [FieldTypes.ID]: string
  [FieldTypes.INITIALS]: string
  [FieldTypes.MULTIPLE_SELECT]: string[]
  [FieldTypes.NUMBER]: number
  [FieldTypes.PERCENTAGE]: number
  [FieldTypes.PHONE]: string
  [FieldTypes.SIGNATURE]: string
  [FieldTypes.TEXT]: string
  [FieldTypes.TEXTAREA]: string
  [FieldTypes.TIME]: string
  [FieldTypes.HEIGHT]: number
  [FieldTypes.EXAM_SECTION]: ExamSectionData
  [FieldTypes.BLOOD_PRESSURE]: BloodPressure
}

export enum FormElementTypes {
  FIELD = 'field',
  FIELD_MAP = 'field-map',
  LIST_FIELD = 'list-field',
  INFO_STAGE = 'info-stage',
}

export type FieldFromType<T extends FieldTypes> = Extract<Field, { type: T }>

export type FieldFormatter = (
  val: any | undefined,
  field: Field,
  collections: AppCollectionsState,
) => any

export type InputFormElement = FieldMap | Field | ListField
export type FormElement = InputFormElement | InfoStage
export interface FormElementProps<StyleProp extends AnyObject> {
  field: FormElement
  root?: boolean
  name?: string
  basePath?: string
  showName?: boolean
  active?: boolean
  disabled?: boolean
  disabledReason?: string
  readOnly?: boolean
  listIndex?: number
  mapIndex?: number
  depth?: number
  style?: StyleProp
  inputStyle?: StyleProp
  showErrorIfClean?: boolean
  prevField?: FormElement
  /** Form-wide input size. When set on a `root` FormElement it propagates to all descendants. */
  size?: InputSize
}

export interface StageElementProps<StyleProp extends AnyObject>
  extends FormElementProps<StyleProp> {
  errors: ValidationErrors
  dirty?: boolean
  setIsDirty?: (dirty: boolean) => void
  setCanSubmit?: (dirty: boolean) => void
}

export interface ListField {
  type?: never
  info?: never
  children?: never
  name: string
  itemFields: FieldMap | Field
  itemName: string
  displayedField?: 'index' | 'alpha-index' | 'alpha' | string
  getTitle?: (value: FieldMapValue | undefined, index: number) => string
  optional?: boolean
  labels?: FieldLabels
  hints?: FieldHints
  condition?: FieldCondition
  horizontalConfig?: {
    itemWidth: number
  }
}

export interface FieldMap<UINode extends BaseUINode = BaseUINode> {
  info?: never
  itemFields?: never
  shortcut?: never
  type?: never
  name: string
  initExpanded?: boolean // for data viewss
  hints?: FieldHints
  labels?: FieldLabels
  condition?: FieldCondition
  toName?: (value: FieldMapValue) => UINode | string
  optional?: boolean
  validate?: (value?: FieldMapValue) => ValidationErrors
  calculate?: Calculation[]
  calculateOnConditional?: boolean
  children: Record<string, Field | FieldMap | ListField | InfoStage>
}

export interface InfoStage {
  type?: never
  children?: never
  itemFields?: never
  info: true
  name: string
  data: CustomElement[]
  style?: CSSProperties
  condition?: FieldCondition
  editorVersion?: 'v1' | 'v2'
}

export type DBStageType = 'text'

export interface DBTextStage {
  text?: Descendant[]
}

export type DBStageContent = DBTextStage

export interface DBStage {
  id: string
  parents?: string[]
  type?: DBStageType
  content?: DBStageContent
}

export interface DBForm {
  name: string
  description?: string
  stages: DBStage[]
  parents?: string[]
}

export interface FieldStructure {
  name: string
  children: FieldStructure[]
}

// Helper type to extract data type from a Field
type FieldDataType<F extends Field> = F extends { type: infer T }
  ? T extends FieldTypes
    ? FieldData[T]
    : never
  : never

// Helper type to extract data type from ListField's itemFields (can be Field or FieldMap)
type ListFieldItemDataType<LF extends ListField> = LF['itemFields'] extends FieldMap
  ? FieldMapData<LF['itemFields']>
  : LF['itemFields'] extends Field
    ? FormElementDataType<LF['itemFields']>
    : never

// Helper type to extract data type from a FormElement (Field, FieldMap, ListField, or InfoStage)
type FormElementDataType<E extends FormElement> = E extends Field
  ? FieldDataType<E>
  : E extends FieldMap
    ? FieldMapData<E>
    : E extends ListField
      ? Array<ListFieldItemDataType<E>>
      : E extends InfoStage
        ? never
        : never

// Type which takes a FieldMap and returns the appropriate data structure for the field map
export type FieldMapData<T extends FieldMap> = {
  [K in keyof T['children']]: FormElementDataType<T['children'][K]>
}
